// Iter 343 (Kamel) — ✈️ Vérification FINALE des informations avant transmission à la compagnie
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VerificationVol() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/verification-vol/${token}`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const confirmer = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/verification-vol/${token}/confirmer`);
      await load();
      window.scrollTo(0, 0);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center p-6"><p className="bg-white rounded-2xl shadow p-8 text-center font-bold text-[#1E3A5F]">😕 {err}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="text-gray-400">Chargement…</p></div>;

  const manquants = data.voyageurs.some(v => v.infos.some(i => i.manquant));

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="verif-vol-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#1E3A5F] text-white rounded-t-2xl p-6">
          <p className="text-sm text-[#79c9ea] font-semibold">Rhône Alpes Amitié Voyages</p>
          <h1 className="text-xl sm:text-2xl font-bold mt-1">✈️ Dernière vérification avant la compagnie aérienne</h1>
          <p className="text-sm mt-2 text-gray-200">Voyage : <strong>{data.voyage}</strong> ({data.dates})</p>
        </div>
        <div className="bg-white rounded-b-2xl shadow-xl p-6 space-y-5">

          {data.confirme_le ? (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 text-center" data-testid="verif-vol-confirme">
              <p className="text-2xl">✅</p>
              <p className="font-bold text-emerald-800">Merci ! Vous avez confirmé vos informations le {data.confirme_le}.</p>
              <p className="text-xs text-emerald-700 mt-1">Vous n'avez plus rien à faire — bon voyage ! 🧳</p>
            </div>
          ) : (
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-sm text-sky-900 leading-relaxed" data-testid="verif-vol-intro">
              🤝 <strong>Un petit geste qui évite de gros soucis.</strong> Comme le font les compagnies aériennes,
              nous vous demandons de <strong>relire une dernière fois</strong> les informations ci-dessous : elles
              seront transmises telles quelles à la compagnie. Une seule lettre ou un chiffre erroné peut
              compliquer l'embarquement — mieux vaut vérifier deux fois maintenant que stresser à l'aéroport 😊
            </div>
          )}

          {data.deadline_label && !data.locked && (
            <p className="text-center text-sm font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="verif-vol-deadline">
              ⏳ Corrections possibles jusqu'au <span className="uppercase">{data.deadline_label}</span> — passé ce délai, le listing part à la compagnie.
            </p>
          )}
          {data.locked && (
            <p className="text-center text-sm font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="verif-vol-locked">
              🔒 La date limite ({data.deadline_label}) est dépassée : le listing a été transmis à la compagnie.
              Pour toute correction indispensable, contactez-nous vite au 06 60 41 62 18.
            </p>
          )}

          {data.voyageurs.map(v => (
            <div key={v.index} className="border-2 border-gray-200 rounded-xl overflow-hidden" data-testid={`verif-vol-voyageur-${v.index}`}>
              <p className="bg-gray-100 px-4 py-2 font-bold text-[#1E3A5F]">🧳 {v.nom} {v.prenom}</p>
              <div className="divide-y">
                <div className="flex justify-between px-4 py-2 text-sm"><span className="text-gray-500">Nom (tel que transmis)</span><strong>{v.nom}</strong></div>
                <div className="flex justify-between px-4 py-2 text-sm"><span className="text-gray-500">Prénom</span><strong>{v.prenom}</strong></div>
                {v.infos.map(i => (
                  <div key={i.label} className="flex justify-between gap-3 px-4 py-2 text-sm">
                    <span className="text-gray-500">{i.label}</span>
                    <strong className={i.manquant ? 'text-red-600' : ''}>{i.valeur}</strong>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {manquants && !data.locked && (
            <p className="text-xs font-bold text-red-600 text-center" data-testid="verif-vol-manquants">
              ⚠️ Des informations sont manquantes : merci de les compléter via le bouton « Corriger » ci-dessous.
            </p>
          )}

          {!data.locked && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              {!data.confirme_le && (
                <button onClick={confirmer} disabled={busy || manquants}
                  className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
                  data-testid="verif-vol-confirmer-btn">
                  ✅ Tout est exact, je confirme
                </button>
              )}
              <Link to={`/mes-pieces/${data.pi_token}`}
                className="px-6 py-3 rounded-xl border-2 border-[#1E3A5F] text-[#1E3A5F] font-bold text-center hover:bg-[#1E3A5F] hover:text-white transition"
                data-testid="verif-vol-corriger-btn">
                ✏️ J'ai repéré une erreur — corriger
              </Link>
            </div>
          )}
          <p className="text-center text-xs text-gray-400">Une question ? WhatsApp : 06 60 41 62 18 — Rhône Alpes Amitié Voyages</p>
        </div>
      </div>
    </div>
  );
}
